import React, { useRef } from "react";
import { FormattedMessage } from "react-intl";
import ProductName from "theme/modules/ProductView/Name";
import { ProductPrice } from "theme/components/atoms/Typography/Price";
import Configurator from "theme/components/organisms/Configurator";
import SimilarColor from "theme/components/organisms/SimilarColor";
import AddToCart from "theme/modules/AddToCart";
import ErrorBoundary from "theme/components/helpers/ErrorBoundary";
import { ErrorAlert } from "theme/components/molecules/Alert";
import AdditionalInformations from "../AdditionalInformations";
import ModalAlertStock from "theme/components/organisms/ModalAlertStock";
import GiftCardSynthesis from "theme/modules/ProductView/GiftCardSynthesis";
import classNames from "classnames";

const formateOptionForSubscribeAlert = (sku, options) => {
  let optionString = sku;
  for (const [key, value] of Object.entries(options)) {
    optionString += `__${key}_${value}`;
  }

  return optionString;
};
const RatingSummary = ({ ratingSummary, reviews }) => {
  return (
    <div
      className="rating-summary"
      itemProp="aggregateRating"
      itemScope
      itemType="https://schema.org/AggregateRating"
      onClick={() => {
        document.querySelector("#tab-review > h5") === null
          ? document.querySelector("#tab-review > button").click()
          : document.querySelector("#tab-review > h5").click();

        document.querySelector("#tab-review").scrollIntoView({});
      }}
    >
      <meta itemProp="reviewCount" content={reviews.length} />
      <meta itemProp="ratingValue" content={ratingSummary} />
      {[...Array(5)].map((star, i) => {
        return ratingSummary >= i + 1 ? (
          <i className="rating-star active" key={i}></i>
        ) : (
          <i className="rating-star" key={i}></i>
        );
      })}
      <span className="rating-summary__value">
        <span>{ratingSummary}</span>
        /5
      </span>
      <span className="rating-summary__count">
        <span>{reviews.length}</span>
        <FormattedMessage
          id="modules.ProductView.ProductItem.Overview.ratingSummary"
          defaultMessage="reviews"
        />
      </span>
    </div>
  );
};

const ProductSynthesis = (props) => {
  const {
    name,
    sku,
    prices,
    options,
    customOptions,
    selectedOptions,
    setOption,
    selectedCustomOptions,
    setCustomOption,
    onProductAdded,
    stock,
    product,
    setPreviewContent,
    previewContent,
    sizeGuideCms,
  } = props;
  const giftCardFormRef = useRef(null);
  return (
    <div className="product__synthesis">
      {!product.display_reproduce_message && (
        <ModalAlertStock
          sku={
            selectedOptions && Object.keys(selectedOptions).length > 0
              ? formateOptionForSubscribeAlert(product.sku, selectedOptions)
              : product.sku
          }
          selectedOptions={selectedOptions}
          currentConfigIsInStock={product.currentConfigIsInStock}
          stock={stock}
        />
      )}

      <ProductName
        name={
          previewContent && previewContent.name ? previewContent.name : name
        }
        sku={previewContent && previewContent.sku ? previewContent.sku : sku}
      />
      {product.reviews && product.ratingSummary ? (
        <RatingSummary
          ratingSummary={product.ratingSummary}
          reviews={product.reviews}
        />
      ) : (
        ""
      )}

      <ErrorBoundary>
        {({ hasError }) => {
          if (hasError) {
            return (
              <ErrorAlert>
                <FormattedMessage
                  id="modules.ProductView.Synthesis.Error"
                  defaultMessage="This product can't be ordered yet, try again later"
                />
              </ErrorAlert>
            );
          }

          return (
            <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
              <div className="product__details">
                <div
                  className={classNames("product__price", {
                    "product__price--hidden":
                      !prices ||
                      !prices.finalPrice?.priceInclTax?.value?.amount,
                  })}
                >
                  {!selectedOptions ||
                  Object.keys(selectedOptions).length > 0 ? (
                    <ProductPrice prices={prices} productPage />
                  ) : (
                    <FormattedMessage
                      id="modules.ProductView.ProductItem.Overview.pricePrefix"
                      defaultMessage="From {price}"
                      values={{ price: <ProductPrice prices={prices} /> }}
                    />
                  )}
                </div>
              </div>

              <div className="product__details__wrapper">
                <div className="product__details__wrapper--left">
                  {product.similarColor && (
                    <SimilarColor
                      sku={sku}
                      products={product.similarColor}
                      maxColorDisplay={28}
                      setPreviewContent={setPreviewContent}
                      previewContent={previewContent}
                      currentProductColorName={
                        product.collegien_color_name
                          ? product.collegien_color_name
                          : false
                      }
                      currentProductColorHexa={
                        product.collegien_color_hexa
                          ? product.collegien_color_hexa
                          : false
                      }
                    />
                  )}
                  {product.display_reproduce_message === true && (
                    <div className="product__open-stock-warning">
                      <ErrorAlert>
                        {product.productPageNoReproduceWarning}
                      </ErrorAlert>
                    </div>
                  )}
                  {options && !product.isGiftCard && (
                    <Configurator
                      options={options}
                      customOptions={customOptions}
                      selectedOptions={selectedOptions}
                      selectedCustomOptions={selectedCustomOptions}
                      setOption={setOption}
                      disabledOutOfStockOptions={
                        product.display_reproduce_message ? true : false
                      }
                      setCustomOption={setCustomOption}
                      product={{
                        attributeSetLabel: product.attributeSetLabel,
                        sizeGuide: product.sizeGuide,
                        price:
                          prices && prices.finalPrice.priceInclTax.value.amount,
                      }}
                    />
                  )}

                  <GiftCardSynthesis
                    giftCardFormRef={giftCardFormRef}
                    product={product}
                    setCustomOption={setCustomOption}
                  />
                  <div className="product__add-to-cart">
                    {stock !== null &&
                    stock !== undefined &&
                    stock.isInStock !== false ? (
                      <AddToCart
                        giftCardFormRef={giftCardFormRef}
                        selectedOptions={selectedOptions}
                        selectedCustomOptions={selectedCustomOptions}
                        onProductAdded={onProductAdded}
                        stock={stock}
                        product={{
                          ...product,
                          sku,
                          isGiftCard: product.isGiftCard,
                          price:
                            prices &&
                            prices.finalPrice.priceInclTax.value.amount,
                          options,
                          customOptions,
                          stock,
                          currentConfigIsInStock:
                            props.product.currentConfigIsInStock,
                        }}
                        label={
                          <FormattedMessage
                            id="modules.ProductView.Synthesis.addToCart"
                            defaultMessage="Add to cart"
                          />
                        }
                        unavailableLabel={
                          <FormattedMessage
                            id="modules.ProductView.Synthesis.unavailable"
                            defaultMessage="Currently unavailable"
                          />
                        }
                      />
                    ) : (
                      <FormattedMessage
                        id="modules.ProductView.Synthesis.unavailable"
                        defaultMessage="Currently unavailable"
                      />
                    )}
                  </div>
                  {product.reassurance && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product.reassurance.content,
                      }}
                    />
                  )}
                  <AdditionalInformations
                    description={product.description}
                    descriptionMedia={product.descriptionMedia}
                    composition={product.composition}
                    composition_percentage={product.composition_percentage}
                    maintenanceTip={product.maintenanceTip}
                    attributeSetId={product.attributeSetId}
                    sizeGuideCms={sizeGuideCms}
                    reviews={product.reviews}
                    intl={props.intl}
                  />
                </div>
              </div>
            </div>
          );
        }}
      </ErrorBoundary>
    </div>
  );
};

export default ProductSynthesis;
